import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import "./mobile-app.css";

// Barre d'onglets + feuille glissante façon application iPhone/Android (téléphone seulement).
const ICONS = {
  home: '<path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z"/>',
  grid: '<rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/>',
  user: '<circle cx="12" cy="8" r="3.5"/><path d="M5 20c.7-3.6 3.4-5.6 7-5.6s6.3 2 7 5.6"/>',
  chat: '<path d="M4 5h16v11H8l-4 4z"/>',
  more: '<circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/>',
  cal: '<rect x="3" y="5" width="18" height="16" rx="3"/><path d="M3 10h18M8 3v4M16 3v4"/>',
  layers: '<path d="M12 3l9 5-9 5-9-5z"/><path d="M3 13l9 5 9-5"/>',
  hand: '<path d="M3 12l5-4 4 2 4-2 5 4-5 5h-4l-2-2-2 2z"/>',
  book: '<path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z"/><path d="M4 19V5"/>',
  phone: '<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z"/>',
  wa: '<path d="M4 20l1.3-4.2A8 8 0 1 1 8.3 18.8z"/>',
  mail: '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>',
};
export const Icone = ({ n }) => <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: ICONS[n] }} />;

const tel = () => matchMedia("(max-width:767px)").matches;

export default function MobileApp({ tabs, sheets = {}, dir = "ltr", label = "Navigation", theme }) {
  const { pathname, hash } = useLocation();
  const [ouverte, setOuverte] = useState(null);
  const feuille = useRef(null);
  const glisse = useRef(null);

  useEffect(() => { setOuverte(null); }, [pathname, hash]);

  // en-tête : s'efface quand on descend, revient quand on remonte
  useEffect(() => {
    let dernier = scrollY;
    const f = () => {
      document.documentElement.classList.toggle("ma-cache", tel() && scrollY > dernier && scrollY > 120);
      dernier = scrollY;
    };
    addEventListener("scroll", f, { passive: true });
    return () => { removeEventListener("scroll", f); document.documentElement.classList.remove("ma-cache"); };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("ma-ouvert", !!ouverte);
    if (ouverte) navigator.vibrate?.(8);
    const echap = (e) => e.key === "Escape" && setOuverte(null);
    addEventListener("keydown", echap);
    return () => removeEventListener("keydown", echap);
  }, [ouverte]);

  const debut = (e) => { glisse.current = { y0: e.clientY, dy: 0, t0: performance.now() }; feuille.current.classList.add("glisse"); };
  const bouge = (e) => { const g = glisse.current; if (!g) return; g.dy = Math.max(0, e.clientY - g.y0); feuille.current.style.transform = `translateY(${g.dy}px)`; };
  const fin = () => {
    const g = glisse.current; if (!g) return;
    feuille.current.classList.remove("glisse"); feuille.current.style.transform = "";
    if (g.dy > 110 || g.dy / (performance.now() - g.t0) > 0.6) setOuverte(null);
    glisse.current = null;
  };

  const s = ouverte && sheets[ouverte];
  const Contenu = ({ it }) => {
    const inner = (<><Icone n={it.icon || "chat"} /><span><b>{it.label}</b>{it.sub && <small dir={it.ltr ? "ltr" : undefined} style={it.ltr ? { unicodeBidi: "isolate", textAlign: "start" } : undefined}>{it.sub}</small>}</span></>);
    const cls = "ma-item" + (it.primary ? " ma-primary" : "");
    if (it.to) return <Link className={cls} to={it.to} state={it.state}>{inner}</Link>;
    if (it.onClick) return <button type="button" className={cls} onClick={() => { setOuverte(null); it.onClick(); }}>{inner}</button>;
    return <a className={cls} href={it.href} {...(it.href?.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}>{inner}</a>;
  };

  return (<div className="ma-root" style={theme}>
    <nav className="ma-tabbar" aria-label={label}>
      {tabs.map((t) => {
        const actif = (ok) => (ok && !(ouverte && t.sheet !== ouverte) ? "on" : undefined);
        const cls = ({ isActive }) => actif(isActive || t.active?.(pathname, hash));
        const inner = (<><Icone n={t.icon} /><span>{t.label}</span></>);
        if (t.sheet) return <button key={t.id} type="button" className={ouverte === t.sheet || (!ouverte && t.active?.(pathname, hash)) ? "on" : undefined} onClick={() => setOuverte((o) => (o === t.sheet ? null : t.sheet))}>{inner}</button>;
        if (t.onClick) return <button key={t.id} type="button" className={actif(t.active?.(pathname, hash))} onClick={t.onClick}>{inner}</button>;
        return <NavLink key={t.id} to={t.to} end className={cls} viewTransition>{inner}</NavLink>;
      })}
    </nav>
    <div className="ma-voile" onClick={() => setOuverte(null)} />
    <div className="ma-sheet" ref={feuille} role="dialog" aria-modal="true" aria-label={s?.title} dir={dir}
      onPointerDown={debut} onPointerMove={bouge} onPointerUp={fin} onPointerCancel={fin}>
      <div className="ma-poignee" aria-hidden="true" />
      {s && <>
        <p className="ma-titre">{s.title}</p>
        {s.langs && <div className="ma-langs">{s.langs.map((l) => (
          <button key={l.code} type="button" className={l.code === s.lang ? "on" : undefined} onClick={() => { s.setLang(l.code); setOuverte(null); }}>{l.label}</button>
        ))}</div>}
        {s.items.map((it) => <Contenu key={it.label} it={it} />)}
      </>}
    </div>
  </div>);
}
